/**
 * src/pages/SignupPage.tsx
 * =============================================================================
 * Formulario de registro (post-migración Better Auth, 2026-05-26).
 *
 * Campos:
 *  - name      → user.name en Better Auth → también genera el nombre
 *                 de la workspace por defecto ("Workspace de {name}")
 *                 vía el hook `databaseHooks.user.create.after` en el Worker.
 *  - email, password → credenciales
 *
 * Flujo:
 *  1. signUp() crea el user en Better Auth y dispara email de verificación.
 *     El hook del Worker crea profile + organization + membership admin.
 *  2. Better Auth devuelve sin sesión (requireEmailVerification=true). La UI
 *     muestra "revisa tu correo" — al verificar el email el usuario queda
 *     auto-logueado (autoSignInAfterVerification=true) y aterriza en /app.
 * =============================================================================
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { signUp } from '@/services/authService'
import { useCaptcha } from '@/hooks/useCaptcha'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'

export function SignupPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Si el user llegó aquí desde un magic link de invitación, llevamos el
  // token en la URL para hacer flow continuo: signup → verify email →
  // /invite/accept?token=... → /d/:id. El callbackURL del email se setea
  // dinámico para que la verificación caiga directa en accept-invite.
  const inviteToken = searchParams.get('invite')

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  // Captcha invisible: se resuelve mientras se rellena el formulario.
  const obtenerCaptcha = useCaptcha()
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!acceptedTerms) return
    setSubmitting(true)
    setError(null)
    try {
      const callbackURL = inviteToken
        ? `/invite/accept?token=${encodeURIComponent(inviteToken)}`
        : undefined
      const result = await signUp({ email, password, name, callbackURL }, await obtenerCaptcha())
      if (result.requiresEmailConfirmation) {
        setEmailSent(true)
      } else {
        navigate(inviteToken ? `/invite/accept?token=${inviteToken}` : '/', { replace: true })
      }
    } catch (err) {
      setError(mapAuthError(err, t))
    } finally {
      setSubmitting(false)
    }
  }

  if (emailSent) {
    return (
      <main className="min-h-dvh flex items-center justify-center px-6">
        <div className="max-w-sm w-full text-center space-y-4 border border-border rounded-lg p-8 bg-card">
          <h1 className="text-xl font-semibold">{t('auth:signup.emailSentTitle')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('auth:signup.emailSentBody', { email })}
          </p>
          <Link to="/login" className="inline-block text-sm underline underline-offset-4">
            {t('auth:signup.loginLink')}
          </Link>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-dvh flex flex-col items-center justify-center px-6 bg-background">
      <Link to="/" className="mb-8 text-base font-bold tracking-tight">
        {t('app.name')}
      </Link>

      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-5 border border-border rounded-lg p-6 bg-card"
      >
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">{t('auth:signup.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('auth:signup.subtitle')}</p>
        </div>

        <div>
          <Label htmlFor="name">{t('auth:signup.fullName')}</Label>
          <Input
            id="name"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="email">{t('auth:signup.email')}</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="password">{t('auth:signup.password')}</Label>
          <Input
            id="password"
            type="password"
            required
            autoComplete="new-password"
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">{t('auth:signup.passwordHint')}</p>
          {password.length > 0 && <PasswordStrengthHint password={password} />}
        </div>

        <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            required
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 rounded border-border accent-foreground cursor-pointer"
          />
          <span className="leading-snug">
            {t('common:legal.acceptPrefix')}{' '}
            <Link
              to="/legal/terminos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline underline-offset-2 hover:text-primary"
            >
              {t('common:legal.terms')}
            </Link>{' '}
            {t('common:legal.acceptConjunction')}{' '}
            <Link
              to="/legal/privacidad"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline underline-offset-2 hover:text-primary"
            >
              {t('common:legal.privacy')}
            </Link>
            .
          </span>
        </label>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button type="submit" className="w-full" disabled={submitting || !acceptedTerms}>
          {submitting ? t('common:actions.loading') : t('auth:signup.submit')}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          {t('auth:signup.hasAccount')}{' '}
          <Link to="/login" className="underline underline-offset-4 hover:text-foreground">
            {t('auth:signup.loginLink')}
          </Link>
        </p>
      </form>
    </main>
  )
}

/**
 * Traduce errores típicos de Better Auth a mensajes i18n. El código semántico
 * llega en `err.code` cuando es ApiError.
 */
function mapAuthError(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    const code = err.code.toLowerCase()
    if (code.includes('user_already_exists') || code.includes('email_already')) {
      return t('auth:errors.emailInUse')
    }
    if (code.includes('password')) return t('auth:errors.weakPassword')
    if (code === 'captcha_invalid') return t('auth:errors.captcha')
    if (code === 'email_domain_blocked') return t('auth:errors.emailNoAdmitido')
    if (code === 'rate_limited') return t('auth:errors.rateLimited')
    return err.message || t('common:errors.generic')
  }
  return err instanceof Error ? err.message : t('common:errors.generic')
}

/**
 * Indicador en línea de fortaleza de contraseña. Heurística simple:
 *  - longitud >= 10
 *  - mezcla mayúscula + minúscula + dígito
 *  - bonus por símbolo
 *
 * Solo feedback visual previo al submit. Better Auth aplica sus propios mínimos
 * en el backend (minPasswordLength) y rechaza el signup si no se cumplen.
 */
function PasswordStrengthHint({ password }: { password: string }) {
  const { t } = useTranslation()
  const longEnough = password.length >= 10
  const hasUpper = /[A-Z]/.test(password)
  const hasLower = /[a-z]/.test(password)
  const hasDigit = /[0-9]/.test(password)
  const hasSymbol = /[^A-Za-z0-9]/.test(password)
  const score = [longEnough, hasUpper, hasLower, hasDigit, hasSymbol].filter(Boolean).length
  const strength =
    score <= 2 ? 'weak' : score === 3 ? 'medium' : score === 4 ? 'strong' : 'very-strong'
  const colorCls =
    strength === 'weak'
      ? 'bg-destructive'
      : strength === 'medium'
        ? 'bg-amber-500'
        : strength === 'strong'
          ? 'bg-green-500'
          : 'bg-green-600'
  const widthCls =
    strength === 'weak'
      ? 'w-1/4'
      : strength === 'medium'
        ? 'w-2/4'
        : strength === 'strong'
          ? 'w-3/4'
          : 'w-full'
  return (
    <div className="mt-1 space-y-1">
      <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${colorCls} ${widthCls} transition-all`} />
      </div>
      {!longEnough && (
        <p className="text-[11px] text-destructive">
          {t('auth:signup.passwordTooShort')}
        </p>
      )}
      {longEnough && !(hasUpper && hasLower && hasDigit) && (
        <p className="text-[11px] text-amber-600">
          {t('auth:signup.passwordMix')}
        </p>
      )}
    </div>
  )
}
